import { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { parseSharedFiltersFromUrl } from 'src/utils/urlFilterUtils';

/**
 * Hook for detecting and parsing shared filters from URL
 *
 * Monitors URL changes and extracts shared filter state when present
 * Returns null when not in shared mode (normal browsing)
 *
 * @param options - Configuration options
 * @returns Shared filter state and metadata
 */
export interface UseSharedFiltersOptions {
  currentBusinessUnitId: string;
}

export interface UseSharedFiltersReturn<T> {
  sharedFilters: T | null;
  isSharedMode: boolean;
  businessUnitId: string | null;
  isBUMismatch: boolean;
}

export function useSharedFilters<T extends Record<string, any>>(
  options: UseSharedFiltersOptions,
): UseSharedFiltersReturn<T> {
  const { currentBusinessUnitId } = options;
  const location = useLocation();

  const [sharedFilters, setSharedFilters] = useState<T | null>(null);
  const [isSharedMode, setIsSharedMode] = useState(false);
  const [businessUnitId, setBusinessUnitId] = useState<string | null>(null);
  const [isBUMismatch, setIsBUMismatch] = useState(false);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const parsed = parseSharedFiltersFromUrl<T>(params);

    if (parsed) {
      // Shared URL detected
      setSharedFilters(parsed.filters);
      setBusinessUnitId(parsed.businessUnitId);
      setIsSharedMode(true);

      // Check for business unit mismatch
      const mismatch =
        !!currentBusinessUnitId && parsed.businessUnitId !== currentBusinessUnitId;
      setIsBUMismatch(!!mismatch);
    } else {
      // Normal mode (no shared filters in URL)
      setSharedFilters(null);
      setBusinessUnitId(null);
      setIsSharedMode(false);
      setIsBUMismatch(false);
    }
  }, [location.search, currentBusinessUnitId]);

  return {
    sharedFilters,
    isSharedMode,
    businessUnitId,
    isBUMismatch,
  };
}
